import React, { useEffect, useState } from "react";

const HOURS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, "0"),
);
const MINUTES = Array.from({ length: 12 }, (_, index) =>
  String(index * 5).padStart(2, "0"),
);
const PERIODS = ["AM", "PM"];

const parseTimeValue = (value) => {
  if (!value) {
    return { hour: "", minute: "", period: "" };
  }

  const [rawHours = "0", rawMinutes = "0"] = value.split(":");
  const hours24 = Number(rawHours);
  const minutes = String(Number(rawMinutes)).padStart(2, "0");
  const period = hours24 >= 12 ? "PM" : "AM";
  const normalizedHours = hours24 % 12 || 12;

  return {
    hour: String(normalizedHours).padStart(2, "0"),
    minute: minutes,
    period,
  };
};

const buildTimeValue = ({ hour, minute, period }) => {
  if (!hour || !minute || !period) {
    return "";
  }

  const normalizedHour = Number(hour) % 12;
  const hours24 = period === "PM" ? normalizedHour + 12 : normalizedHour;

  return `${String(hours24).padStart(2, "0")}:${minute}:00`;
};

const getPossibleValues = (field, currentValue) => {
  if (currentValue) {
    return [currentValue];
  }

  if (field === "hour") {
    return HOURS;
  }

  if (field === "minute") {
    return MINUTES;
  }

  return PERIODS;
};

const isOptionSelectable = (nextParts, minTimeExclusive) => {
  if (!minTimeExclusive) {
    return true;
  }

  const hours = getPossibleValues("hour", nextParts.hour);
  const minutes = getPossibleValues("minute", nextParts.minute);
  const periods = getPossibleValues("period", nextParts.period);
  const minValue = parseTimeValue(minTimeExclusive);
  const minTime = buildTimeValue(minValue);

  return hours.some((hour) =>
    minutes.some((minute) =>
      periods.some((period) => {
        const candidate = buildTimeValue({ hour, minute, period });
        return candidate && candidate > minTime;
      }),
    ),
  );
};

const fieldBaseClasses = (hasError) =>
  [
    "h-11 rounded-lg border bg-white text-base text-slate-900 shadow-sm outline-none transition",
    hasError
      ? "border-rose-300 focus:border-rose-400"
      : "border-slate-200 focus:border-slate-400",
  ].join(" ");

const TimePicker = ({
  label,
  helper,
  error,
  value,
  onChange,
  className = "",
  minTimeExclusive = "",
  layout = "stacked",
}) => {
  const [parts, setParts] = useState(() => parseTimeValue(value));

  useEffect(() => {
    setParts(parseTimeValue(value));
  }, [value]);

  const updatePart = (patch) => {
    setParts((current) => {
      const next = { ...current, ...patch };
      onChange?.(buildTimeValue(next));
      return next;
    });
  };

  const isHourDisabled = (hour) =>
    !isOptionSelectable({ ...parts, hour }, minTimeExclusive);
  const isMinuteDisabled = (minute) =>
    !isOptionSelectable({ ...parts, minute }, minTimeExclusive);
  const isPeriodDisabled = (period) =>
    !isOptionSelectable({ ...parts, period }, minTimeExclusive);
  const isInline = layout === "inline";
  const fieldRowClass = isInline
    ? "grid min-w-0 items-center grid-cols-[minmax(0,88px)_12px_minmax(0,88px)_minmax(0,132px)] gap-x-1.5"
    : "grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-3";
  const selectClassName =
    `${fieldBaseClasses(Boolean(error))} min-w-0 w-full appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%3E%3cpath%20d%3D%22M7%207l3%203%203-3%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3c%2Fsvg%3E')] bg-[length:1.5em] bg-[right_0.5rem_center] bg-no-repeat ${isInline ? "px-3 pr-8" : "px-4 pr-10"}`;
  const periodGroupClass = isInline
    ? "grid h-11 grid-cols-2 rounded-lg border border-slate-200 bg-slate-50 p-1 shadow-sm"
    : "col-span-3 grid grid-cols-2 rounded-lg border border-slate-200 bg-slate-50 p-1 shadow-sm";

  return (
    <label className={`flex min-w-0 flex-col gap-2 text-sm text-slate-600 ${className}`.trim()}>
      {label}
      <div className={fieldRowClass}>
        <select
          className={selectClassName}
          value={parts.hour}
          onChange={(e) => updatePart({ hour: e.target.value })}
        >
          <option value="">HH</option>
          {HOURS.map((hour) => (
            <option key={hour} value={hour} disabled={isHourDisabled(hour)}>
              {hour}
            </option>
          ))}
        </select>

        <span className="flex items-center justify-center text-base font-semibold text-slate-400">:</span>

        <select
          className={selectClassName}
          value={parts.minute}
          onChange={(e) => updatePart({ minute: e.target.value })}
        >
          <option value="">MM</option>
          {MINUTES.map((minute) => (
            <option key={minute} value={minute} disabled={isMinuteDisabled(minute)}>
              {minute}
            </option>
          ))}
        </select>

        <div className={periodGroupClass}>
          {PERIODS.map((period) => {
            const isActive = parts.period === period;
            const isDisabled = isPeriodDisabled(period);

            return (
              <button
                key={period}
                type="button"
                disabled={isDisabled}
                className={[
                  "rounded-md px-3 py-2 text-sm font-medium transition",
                  isActive
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white hover:text-slate-900",
                  isDisabled ? "cursor-not-allowed opacity-50 hover:bg-transparent hover:text-slate-600" : "",
                ].join(" ")}
                onClick={() => updatePart({ period })}
              >
                {period}
              </button>
            );
          })}
        </div>
      </div>
      {helper && !error && <span className="text-xs text-slate-500">{helper}</span>}
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </label>
  );
};

export default TimePicker;
